import {
  calculateStandardAddonCost,
  calculateStandardTranslationCost,
  CERTIFIED_ORDER_DELIVERY_OPTIONS,
  DELIVERY_OPTIONS_CERTIFIED,
  DELIVERY_OPTIONS_STANDARD,
  STANDARD_ORDER_DELIVERY_OPTIONS,
  TRANSITION_SPEED,
  TRANSLATION_OPTIONS,
  TRANSLATION_RATES,
} from "./constants";
import type {
  deliverType,
  DeliveryFlags,
  translationType,
  TransitionSpeed,
} from "./types";
import { addDays, addHours } from "date-fns";

export const updateStepQueryParam = (step: number) => {
  if (typeof window === "undefined") { 
    return;
  }

  const url = new URL(window.location.href);
  url.searchParams.set("step", step.toString());
  window.history.replaceState(null, "", url.toString());
};

export const getDeliveryFlags = (
  delivery: string[] | undefined,
  isCertified: boolean
): DeliveryFlags => {
  if (isCertified) {
    const expeditedKey = DELIVERY_OPTIONS_CERTIFIED.expeditedTurnaround;
    const hasExpedited = expeditedKey ? delivery?.includes(expeditedKey) : null;

    return {
      isNotarized:
        delivery?.includes(DELIVERY_OPTIONS_CERTIFIED.notarization) ?? false,
      is2DayShipping: false,
      isMailHardCopy:
        delivery?.includes(DELIVERY_OPTIONS_CERTIFIED.mailHardCopy) ?? false,
      desktopPublishing: delivery?.includes(DELIVERY_OPTIONS_CERTIFIED.desktopPublishing) ?? false,
      proofreading: delivery?.includes(DELIVERY_OPTIONS_CERTIFIED.proofreading) ?? false,
      transitionSpeed:
        hasExpedited === null
          ? null
          : hasExpedited
          ? TRANSITION_SPEED.express
          : TRANSITION_SPEED.standard,
    };
  }

  const expeditedKey = DELIVERY_OPTIONS_STANDARD.expeditedDelivery;
  const hasExpedited = expeditedKey ? delivery?.includes(expeditedKey) : null;

  return {
    isNotarized: false,
    is2DayShipping: false,
    isMailHardCopy: false,
    transitionSpeed:
      hasExpedited === null
        ? null
        : hasExpedited
        ? TRANSITION_SPEED.express
        : TRANSITION_SPEED.standard,
    desktopPublishing: delivery?.includes(DELIVERY_OPTIONS_STANDARD.desktopPublishing) ?? false,
    proofreading: delivery?.includes(DELIVERY_OPTIONS_STANDARD.proofreading) ?? false,
  };
};

export const calculateOrderTotal = ({
  translationType,
  pageCount,
  wordCount,
  delivery = [],
}: {
  translationType: translationType;
  pageCount?: number;
  wordCount?: number;
  delivery?: deliverType[];
}) => {
  const isCertified = translationType === TRANSLATION_OPTIONS.certified;

  // Certified: per-page, no floor. Standard: per-word with a $25 (1-page)
  // floor — see calculateStandardTranslationCost for the canonical rule
  // (PROD-2026-05-18-003).
  const translationCost = isCertified
    ? (pageCount ?? 0) * TRANSLATION_RATES.certified
    : calculateStandardTranslationCost(wordCount ?? 0);

  const deliveryOptions = isCertified
    ? CERTIFIED_ORDER_DELIVERY_OPTIONS.filter(({ value }) =>
        delivery.includes(value)
      )
    : STANDARD_ORDER_DELIVERY_OPTIONS.filter(({ value }) =>
        delivery.includes(value)
      );

  const deliveryCost = deliveryOptions.reduce((sum, opt) => {
    // Certified expedited turnaround stays per-page.
    if (isCertified && opt.value === DELIVERY_OPTIONS_CERTIFIED.expeditedTurnaround) {
      return sum + (opt.rate ?? 0) * (pageCount ?? 0);
    }

    // Standard add-ons: per-word with $10 floor (PROD-2026-05-18-002).
    // Helper is shared with OptionsForm + OrderSummary so the displayed
    // card price, the summary row, and the checkout total all derive
    // from the same math.
    if (!isCertified) {
      return sum + calculateStandardAddonCost(opt.rate ?? 0, wordCount ?? 0);
    }

    return sum + (opt.rate ?? 0);
  }, 0);

  return parseFloat((translationCost + deliveryCost).toFixed(2));
};

export type DueDateParams = {
  transitionType: translationType;
  transitionSpeed: TransitionSpeed;
  totalPage?: number;
  totalWords?: number;
  startDate?: Date;
};

export const calculateDueDate = ({
  transitionType,
  transitionSpeed,
  totalPage = 0,
  totalWords = 0,
  startDate = new Date(),
}: DueDateParams): Date | null => {
  const isExpedited = transitionSpeed === "express";
  let dueDate = startDate;

  if (transitionType === "certified") {
    // New business rules based on page count
    if (totalPage >= 1 && totalPage <= 5) {
      // 1-5 pages: Standard 24h, Expedited 12h
      dueDate = addHours(dueDate, isExpedited ? 12 : 24);
    } else if (totalPage >= 6 && totalPage <= 10) {
      // 6-10 pages: Standard 2 business days, Expedited 24h
      if (isExpedited) {
        dueDate = addHours(dueDate, 24);
      } else {
        // Add 2 business days
        let businessDaysAdded = 0;
        while (businessDaysAdded < 2) {
          dueDate = addDays(dueDate, 1);
          // Skip weekends (0 = Sunday, 6 = Saturday)
          const dayOfWeek = dueDate.getDay();
          if (dayOfWeek !== 0 && dayOfWeek !== 6) {
            businessDaysAdded++;
          }
        }
      }
    } else if (totalPage >= 11 && totalPage <= 15) {
      // 11-15 pages: Standard 3 business days, Expedited 2 business days
      const businessDaysToAdd = isExpedited ? 2 : 3;
      let businessDaysAdded = 0;
      while (businessDaysAdded < businessDaysToAdd) {
        dueDate = addDays(dueDate, 1);
        // Skip weekends (0 = Sunday, 6 = Saturday)
        const dayOfWeek = dueDate.getDay();
        if (dayOfWeek !== 0 && dayOfWeek !== 6) {
          businessDaysAdded++;
        }
      }
    } else if (totalPage >= 16) {
      // 16+ pages: Custom delivery date (PM will contact customer)
      return null;
    }
  }

  if (transitionType === "standard") {
    // Standard translation uses word count, convert to page equivalent (250 words/page)
    const pageEquivalent = Math.ceil(totalWords / 250);
    
    if (pageEquivalent >= 1 && pageEquivalent <= 5) {
      // 1-5 pages: Standard 24h, Expedited 12h
      dueDate = addHours(dueDate, isExpedited ? 12 : 24);
    } else if (pageEquivalent >= 6 && pageEquivalent <= 10) {
      // 6-10 pages: Standard 2 business days, Expedited 24h
      if (isExpedited) {
        dueDate = addHours(dueDate, 24);
      } else {
        // Add 2 business days
        let businessDaysAdded = 0;
        while (businessDaysAdded < 2) {
          dueDate = addDays(dueDate, 1);
          const dayOfWeek = dueDate.getDay();
          if (dayOfWeek !== 0 && dayOfWeek !== 6) {
            businessDaysAdded++;
          }
        }
      }
    } else if (pageEquivalent >= 11 && pageEquivalent <= 15) {
      // 11-15 pages: Standard 3 business days, Expedited 2 business days
      const businessDaysToAdd = isExpedited ? 2 : 3;
      let businessDaysAdded = 0;
      while (businessDaysAdded < businessDaysToAdd) {
        dueDate = addDays(dueDate, 1);
        const dayOfWeek = dueDate.getDay();
        if (dayOfWeek !== 0 && dayOfWeek !== 6) {
          businessDaysAdded++;
        }
      }
    } else if (pageEquivalent >= 16) {
      // 16+ pages: Custom delivery date (PM will contact customer)
      return null;
    }
  }

  return dueDate;
};

export const getBrowserType = (): "chrome" | "safari" | "other" => {
  if (typeof window === "undefined") {
    return "other";
  }

  const userAgent = window.navigator.userAgent.toLowerCase();
  const isChrome = /chrome/.test(userAgent) && !/edg/.test(userAgent);
  const isSafari = /safari/.test(userAgent) && !/chrome/.test(userAgent);

  if (isChrome) {
    return "chrome";
  }
  if (isSafari) {
    return "safari";
  }
  return "other";
};
